import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom';

import { Box, Button, Tab, Tabs } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import FlagIcon from '@material-ui/icons/Flag';
import { TEACHERS_PERMISSION_DENIED_URL } from 'config/urls';
import { TEACHERS_STUDENTS_DETAIL } from 'config/urls';
import { useSectionDetail, useStudent } from 'sdk';
import { cutText } from 'utils';

import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import GoalsTable from 'pages/Teachers/StudentDetail/components/GoalsTable';
import StudentSubtitle from 'pages/Teachers/StudentDetail/components/StudentSubtitle';
import { ReactComponent as BackIcon } from 'pages/Teachers/StudentListV2/assets/back-icon.svg';
import StudentsPortfolio from 'pages/Teachers/StudentPortfolio';
import { useNavbar } from 'pages/Teachers/shared';
import AddGoalDialog from 'pages/Teachers/shared/AddGoalDialog';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import UserContext from 'components/UserContext';

import { ReactComponent as PortfolioIcon } from './assets/portfolio.svg';
import styles from './styles.module.scss';

const StudentDetail = () => {
  const user = useContext(UserContext);
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const { studentId, sectionId } = match.params;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);

  const { data: sectionDetail, isLoading: isSectionDetailLoading } =
    useSectionDetail({
      sectionId: match.params.sectionId
    });

  useEffect(() => {
    if (!sectionDetail) return;
    if (activeTabIndex === 0 && sectionDetail?.is_math) {
      trackMixpanelEvent(user.id, 'User views goals page', {
        feature: 'students',
        view: 'goals',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
      });
    }
    if (
      activeTabIndex === 1 ||
      (activeTabIndex === 0 && !sectionDetail?.is_math)
    ) {
      trackMixpanelEvent(user.id, 'User views portfolio page', {
        feature: 'students',
        view: 'portfolio',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
      });
    }
  }, [activeTabIndex, sectionDetail, user.id]);

  const { data: student } = useStudent({
    studentId
  });

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title="Student Goal Progress"
          subtitle={
            <StudentSubtitle
              studentName={student?.name}
              sectionLabel={cutText({ text: sectionDetail?.name, char: 50 })}
              fullLabel={sectionDetail?.name}
            />
          }
          Icon={BackIcon}
        />
      ),
      isTransparent: true
    }),
    [student?.name, sectionDetail?.name]
  );

  useNavbar(navConfig);

  if (user.is_in_free_trial) {
    return <Redirect to={TEACHERS_PERMISSION_DENIED_URL} />;
  }

  const handleTabChange = ({ newValue }) => {
    setActiveTabIndex(newValue);

    if (newValue === 0 && location.search) {
      history.replace({
        pathname: location.pathname,
        state: location.state
      });
    }
  };

  const handleAddGoalClick = () => {
    setIsGoalDialogOpen(true);
    trackMixpanelEvent(user.id, 'User starts to add a goal', {
      feature: 'students',
      view: 'goals',
      button: 'add',
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_STUDENTS_DETAIL
    });
  };

  return (
    <>
      {isGoalDialogOpen && (
        <AddGoalDialog
          isOpen={isGoalDialogOpen}
          onClose={() => setIsGoalDialogOpen(false)}
          studentId={student?.id}
        />
      )}
      <div className={styles.studentDetailWrapper}>
        <div className={styles.studentDetailContainer}>
          {isSectionDetailLoading ? (
            <div className={styles.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <div className={styles.studentDetailHeader}>
                <Tabs
                  value={activeTabIndex}
                  onChange={(event, value) => {
                    handleTabChange({ newValue: value });
                  }}
                  aria-label="student detail tabs"
                  indicatorColor="inherit"
                  textColor="inherit"
                  classes={{ indicator: styles.customTabIndicator }}
                >
                  {sectionDetail?.is_math && (
                    <Tab
                      icon={<FlagIcon />}
                      label="Goals"
                      id="student-tab-0"
                      aria-controls="student-tabpanel-0"
                      className={styles.studentTab}
                    />
                  )}
                  <Tab
                    icon={<PortfolioIcon />}
                    label="Portfolio"
                    id="student-tab-1"
                    aria-controls="student-tabpanel-1"
                    className={styles.studentTab}
                  />
                </Tabs>

                {activeTabIndex === 0 && sectionDetail?.is_math && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    className={styles.addGoalButton}
                    onClick={handleAddGoalClick}
                  >
                    ADD GOAL
                  </Button>
                )}
              </div>

              <Box
                role="tabpanel"
                hidden={activeTabIndex !== 0}
                id="student-tabpanel-0"
                aria-labelledby="student-tab-0"
                p={3}
              >
                {activeTabIndex === 0 && sectionDetail?.is_math && (
                  <GoalsTable studentId={studentId} sectionId={sectionId} />
                )}
              </Box>

              <Box
                role="tabpanel"
                hidden={activeTabIndex !== 1 && sectionDetail?.is_math}
                id="student-tabpanel-1"
                aria-labelledby="student-tab-1"
                p={0}
              >
                {((activeTabIndex === 1 && sectionDetail?.is_math) ||
                  !sectionDetail?.is_math) && (
                  <StudentsPortfolio
                    propStudentId={studentId}
                    history={history}
                    match={match}
                    isEmbedded={true}
                  />
                )}
              </Box>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentDetail;
